<template>
  <div class="app-container dida-order-monitor">
    <el-alert
      class="scope-alert"
      title="Dida普通订单由Dida主动调用我方下单/取消接口。本页用于只读观测与排障，不提供重复下单或反向推送按钮，避免重复供应商订单和重复扣款。"
      type="info"
      :closable="false"
      show-icon
    />

    <div class="filter-container order-filters">
      <el-input v-model="listQuery.booking_id" class="filter-item" clearable placeholder="Dida BookingID" size="small" />
      <el-input v-model="listQuery.system_order_no" class="filter-item" clearable placeholder="系统订单号" size="small" />
      <el-input v-model="listQuery.supplier_order_no" class="filter-item" clearable placeholder="供应商单号/确认号" size="small" />
      <el-input v-model="listQuery.trace_id" class="filter-item" clearable placeholder="Trace ID（精确）" size="small" />
      <el-select v-model="listQuery.action" class="filter-item" clearable placeholder="Dida动作" size="small">
        <el-option label="新订" value="New" />
        <el-option label="取消" value="Cancel" />
        <el-option label="修改" value="Modify" />
      </el-select>
      <el-select v-model="listQuery.target_type" class="filter-item" clearable placeholder="供应商" size="small">
        <el-option label="锦江" value="jin_jiang" />
        <el-option label="龙腾" value="long_teng" />
        <el-option label="隧穿" value="sui_chuan" />
        <el-option label="丽呈" value="li_cheng" />
      </el-select>
      <el-select v-model="listQuery.health_status" class="filter-item" clearable placeholder="健康状态" size="small">
        <el-option label="正常" value="NORMAL" />
        <el-option label="处理中" value="PROCESSING" />
        <el-option label="异常" value="ERROR" />
      </el-select>
      <el-select v-model="listQuery.system_order_status" class="filter-item" clearable placeholder="系统状态" size="small">
        <el-option v-for="item in orderStatuses" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        class="filter-item date-filter"
        type="datetimerange"
        range-separator="至"
        start-placeholder="创建开始"
        end-placeholder="创建结束"
        value-format="yyyy-MM-dd HH:mm:ss"
        size="small"
      />
      <el-button class="filter-item" type="primary" size="small" @click="handleFilter">查询</el-button>
      <el-button class="filter-item" size="small" @click="resetFilter">重置</el-button>
      <el-popover placement="bottom-end" title="订单链路说明" width="500" trigger="click">
        <div class="operation-help">
          <p><b>查询范围：</b>默认展示最近30天；填写任一订单号或Trace ID可跨时间精确排查。</p>
          <p><b>链路顺序：</b>Dida入站鉴权与审计 → 幂等校验 → 供应商下单/取消 → 本地订单与关联落库 → 额度/账户流水 → 响应Dida → 自动查询与对账收敛。</p>
          <p><b>健康状态：</b>“处理中”表示仍处于下单/取消中间态；“异常”表示入站响应未完成、订单失败或存在处理错误。</p>
          <p><b>Trace ID：</b>可关联Dida入站、供应商调用、数据库状态、重试/对账和告警。页面不会展示客人、电话、证件、密码或支付卡信息。</p>
          <p><b>人工处理：</b>先查看详情中的金额核对、关联、额度、字段日志与Trace ID。普通即时确认订单禁止重复下单或反向回调Dida；资源推送失败请到“酒店推送控制”重试。</p>
        </div>
        <el-button slot="reference" class="filter-item" type="text" icon="el-icon-question">操作说明</el-button>
      </el-popover>
    </div>

    <el-table v-loading="listLoading" :data="list" border fit highlight-current-row size="small">
      <el-table-column label="健康" width="90" align="center">
        <template slot-scope="scope"><el-tag :type="healthTag(scope.row.health_status)">{{ healthName(scope.row.health_status) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="BookingID" min-width="185" prop="order_id" show-overflow-tooltip />
      <el-table-column label="Trace ID" min-width="190" prop="trace_id" show-overflow-tooltip />
      <el-table-column label="动作" width="90" align="center">
        <template slot-scope="scope"><el-tag size="mini" :type="actionTag(scope.row.webhook_action)">{{ actionName(scope.row.webhook_action) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="系统状态" width="120" align="center">
        <template slot-scope="scope"><el-tag size="mini" :type="statusTag(scope.row.system_order_status)">{{ statusName(scope.row.system_order_status) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="供应商" width="100" align="center">
        <template slot-scope="scope"><el-tag size="mini" effect="plain" :type="supplierTag(scope.row.target_type)">{{ supplierName(scope.row.target_type) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="酒店" min-width="190" show-overflow-tooltip><template slot-scope="scope">{{ scope.row.hotel_name || scope.row.actual_inn_id || scope.row.inn_id || '-' }}</template></el-table-column>
      <el-table-column label="入住/离店" width="190" align="center"><template slot-scope="scope">{{ scope.row.travel_date }} / {{ scope.row.end_travel_date }}</template></el-table-column>
      <el-table-column label="房间" width="70" prop="ticket_num" align="center" />
      <el-table-column label="渠道总价" width="100" align="right"><template slot-scope="scope">{{ money(scope.row.channel_money) }}</template></el-table-column>
      <el-table-column label="系统订单号" min-width="175" prop="system_order_no" show-overflow-tooltip />
      <el-table-column label="确认号" min-width="130" prop="order_confirm_no" show-overflow-tooltip />
      <el-table-column label="更新时间" width="165" prop="updated_at" align="center" />
      <el-table-column label="操作" width="85" fixed="right" align="center"><template slot-scope="scope"><el-button type="text" size="mini" @click="openDetail(scope.row)">链路详情</el-button></template></el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="listQuery.limit"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog title="Dida订单完整链路（脱敏）" :visible.sync="detailVisible" width="88%" top="4vh">
      <div v-loading="detailLoading">
        <template v-if="detail && detail.order">
          <el-alert v-if="detail.issues.length" class="detail-alert" title="发现需要处理或确认的问题" type="error" :closable="false" show-icon>
            <div v-for="(issue, index) in detail.issues" :key="index">{{ index + 1 }}. {{ issue }}</div>
          </el-alert>
          <el-alert v-else class="detail-alert" title="本地持久化与金额核对未发现异常；Dida最终状态仍需以双方对账结果为准" type="success" :closable="false" show-icon />

          <el-descriptions title="订单概览" :column="4" border size="small">
            <el-descriptions-item label="BookingID">{{ detail.order.order_id }}</el-descriptions-item>
            <el-descriptions-item label="系统订单号">{{ detail.order.system_order_no || '-' }}</el-descriptions-item>
            <el-descriptions-item label="供应商订单号">{{ detail.order.order_code || '-' }}</el-descriptions-item>
            <el-descriptions-item label="供应商确认号">{{ detail.order.order_confirm_no || '-' }}</el-descriptions-item>
            <el-descriptions-item label="系统状态"><el-tag size="mini" :type="statusTag(detail.order.system_order_status)">{{ statusName(detail.order.system_order_status) }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="供应商"><el-tag size="mini" effect="plain" :type="supplierTag(detail.order.target_type)">{{ supplierName(detail.order.target_type) }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="业务状态"><el-tag size="mini" :type="orderStateTag(detail.order.order_state)">{{ orderStateName(detail.order.order_state) }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="支付状态"><el-tag size="mini" :type="payStateTag(detail.order.pay_state)">{{ payStateName(detail.order.pay_state) }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="酒店">{{ detail.order.hotel_name || detail.order.actual_inn_id || '-' }}</el-descriptions-item>
            <el-descriptions-item label="房型/产品">{{ detail.order.room_type_code || '-' }} / {{ detail.order.product_code || '-' }}</el-descriptions-item>
            <el-descriptions-item label="入住/离店">{{ detail.order.travel_date }} / {{ detail.order.end_travel_date }}</el-descriptions-item>
            <el-descriptions-item label="渠道总价">{{ money(detail.order.channel_money) }}</el-descriptions-item>
            <el-descriptions-item label="系统成本">{{ money(detail.order.system_channel_price) }}</el-descriptions-item>
            <el-descriptions-item label="额度状态"><el-tag size="mini" :type="quotaTag(detail.order.quota_status)">{{ quotaStatusName(detail.order.quota_status) }}</el-tag> / {{ money(detail.order.quota_value) }}</el-descriptions-item>
            <el-descriptions-item label="取消状态"><el-tag size="mini" :type="cancelTag(detail.order.system_order_cancel_status)">{{ cancelStatusName(detail.order.system_order_cancel_status) }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="人工覆盖"><el-tag size="mini" :type="detail.order.manual_override_status ? 'warning' : 'info'">{{ detail.order.manual_override_status ? '已人工覆盖' : '未人工覆盖' }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="异常处理"><el-tag size="mini" :type="detail.order.error_order_handle_status ? 'success' : 'info'">{{ detail.order.error_order_handle_status ? '已处理' : '未处理' }}</el-tag></el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ detail.order.created_at || '-' }}</el-descriptions-item>
            <el-descriptions-item label="更新时间">{{ detail.order.updated_at || '-' }}</el-descriptions-item>
            <el-descriptions-item label="取消账务对平">
              <span v-if="!detail.cancellation_reconciliation.applicable">不适用</span>
              <el-tag v-else :type="detail.cancellation_reconciliation.matched ? 'success' : 'danger'">
                {{ detail.cancellation_reconciliation.matched ? '已对平' : '未对平' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.order.handle_error_message" label="处理错误" :span="4"><span class="error-text">{{ detail.order.handle_error_message }}</span></el-descriptions-item>
            <el-descriptions-item v-if="detail.order.refund_message" label="退款说明" :span="4">{{ detail.order.refund_message }}</el-descriptions-item>
          </el-descriptions>

          <h4>入站与响应链路</h4>
          <el-timeline>
            <el-timeline-item v-for="event in detail.webhook_events" :key="event.id" :timestamp="event.created_at" :type="event.completed_at ? 'success' : 'danger'">
              <el-tag size="mini" :type="actionTag(event.action)">{{ actionName(event.action) }}</el-tag>
              <code>Trace ID {{ event.trace_id }}</code>
              <el-tag size="mini" :type="event.completed_at ? 'success' : 'danger'">{{ event.completed_at ? '响应完成' : '响应未完成' }}</el-tag>
              <span v-if="event.completed_at">{{ event.completed_at }}</span>
            </el-timeline-item>
          </el-timeline>

          <h4 class="section-title">金额与持久化核对 <el-tag size="mini" :type="detail.money_reconciliation.matched ? 'success' : 'danger'">{{ detail.money_reconciliation.matched ? '金额已对平' : '金额未对平' }}</el-tag></h4>
          <el-row :gutter="12" class="metric-cards">
            <el-col :span="4"><el-card shadow="never"><b>{{ detail.money_reconciliation.channel_total }}</b><span>渠道总价</span></el-card></el-col>
            <el-col :span="4"><el-card shadow="never"><b>{{ detail.money_reconciliation.nightly_sell_total }}</b><span>逐晚售价合计</span></el-card></el-col>
            <el-col :span="4"><el-card shadow="never"><b>{{ detail.money_reconciliation.nightly_cost_total }}</b><span>逐晚成本合计</span></el-card></el-col>
            <el-col :span="4"><el-card shadow="never"><b>{{ detail.money_reconciliation.stored_nights }}/{{ detail.money_reconciliation.expected_nights }}</b><span>已存/应存晚数</span></el-card></el-col>
            <el-col :span="4"><el-card shadow="never"><b>{{ detail.relations.length }}</b><span>供应商关联</span></el-card></el-col>
            <el-col :span="4"><el-card shadow="never"><b>{{ detail.phone_usage_count }}</b><span>手机号池记录</span></el-card></el-col>
          </el-row>

          <el-tabs>
            <el-tab-pane label="逐晚价格">
              <el-table :data="detail.night_prices" border size="small"><el-table-column prop="night_date" label="日期" /><el-table-column prop="sell_price" label="单间售价" /><el-table-column prop="cost_price" label="单间成本" /><el-table-column prop="profit" label="单间利润" /></el-table>
            </el-tab-pane>
            <el-tab-pane :label="`额度日志 (${detail.quota_logs.length})`">
              <el-table :data="detail.quota_logs" border size="small">
                <el-table-column prop="id" label="ID" width="80" />
                <el-table-column label="方向" width="90" align="center"><template slot-scope="scope"><el-tag size="mini" :type="quotaDirectionTag(scope.row)">{{ quotaDirectionName(scope.row) }}</el-tag></template></el-table-column>
                <el-table-column prop="before_quota_value" label="变更前" />
                <el-table-column prop="after_quota_value" label="变更后" />
                <el-table-column prop="app_client_account_log_no" label="账户流水号" />
                <el-table-column prop="created_at" label="时间" />
              </el-table>
            </el-tab-pane>
            <el-tab-pane :label="`账户流水 (${detail.account_logs.length})`">
              <el-table :data="detail.account_logs" border size="small">
                <el-table-column prop="log_no" label="流水号" min-width="170" />
                <el-table-column label="方向" width="85" align="center"><template slot-scope="scope"><el-tag size="mini" :type="accountDirectionTag(scope.row.is_pay_out)">{{ accountDirectionName(scope.row.is_pay_out) }}</el-tag></template></el-table-column>
                <el-table-column label="业务类型" min-width="125"><template slot-scope="scope">{{ accountLogTypeName(scope.row.type) }}</template></el-table-column>
                <el-table-column prop="before" label="变更前" />
                <el-table-column prop="after" label="变更后" />
                <el-table-column prop="change" label="变更值" />
                <el-table-column label="状态" width="90"><template slot-scope="scope"><el-tag size="mini" :type="accountLogTag(scope.row.status)">{{ accountLogStatusName(scope.row.status) }}</el-tag></template></el-table-column>
                <el-table-column prop="revoke_value" label="撤回值" />
                <el-table-column prop="created_at" label="时间" min-width="155" />
              </el-table>
            </el-tab-pane>
            <el-tab-pane :label="`状态日志 (${detail.field_logs.length})`">
              <el-table :data="detail.field_logs" border size="small">
                <el-table-column prop="id" label="ID" width="75" />
                <el-table-column label="来源" width="100" align="center"><template slot-scope="scope"><el-tag size="mini" effect="plain">{{ fieldSourceName(scope.row.source) }}</el-tag></template></el-table-column>
                <el-table-column label="大类" width="105" align="center"><template slot-scope="scope"><el-tag size="mini" :type="changeCategoryTag(scope.row.change_category)">{{ changeCategoryName(scope.row.change_category) }}</el-tag></template></el-table-column>
                <el-table-column label="操作类型" min-width="155"><template slot-scope="scope">{{ changeTypeName(scope.row.change_type) }}</template></el-table-column>
                <el-table-column label="操作方" width="100"><template slot-scope="scope">{{ operatorTypeName(scope.row.operator_type) }}</template></el-table-column>
                <el-table-column prop="changed_fields" label="变更字段" min-width="220" show-overflow-tooltip />
                <el-table-column prop="created_at" label="时间" width="165" />
              </el-table>
            </el-tab-pane>
            <el-tab-pane :label="`供应商关联 (${detail.relations.length})`">
              <el-table :data="detail.relations" border size="small">
                <el-table-column label="供应商"><template slot-scope="scope"><el-tag size="mini" effect="plain" :type="supplierTag(scope.row.target_type)">{{ supplierName(scope.row.target_type) }}</el-tag></template></el-table-column>
                <el-table-column prop="target_id" label="供应商记录ID" />
                <el-table-column prop="created_at" label="创建时间" />
              </el-table>
            </el-tab-pane>
          </el-tabs>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  data() {
    return {
      orderStatuses: [
        { value: 100, label: '下单处理中' }, { value: 101, label: '下单成功' }, { value: 102, label: '下单失败' },
        { value: 201, label: '取消成功' }, { value: 202, label: '取消失败' }, { value: 203, label: '取消处理中' },
      ],
      list: [],
      total: 0,
      listLoading: false,
      listQuery: { page: 1, limit: 20, booking_id: '', system_order_no: '', supplier_order_no: '', trace_id: '', action: '', target_type: '', health_status: '', system_order_status: '' },
      dateRange: [],
      detailVisible: false,
      detailLoading: false,
      detail: null,
      refreshTimer: null,
    }
  },
  created() {
    this.getList()
    this.refreshTimer = setInterval(() => {
      if (!this.detailVisible) this.getList(false)
    }, 15000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    getList(showLoading = true) {
      if (showLoading) this.listLoading = true
      const params = { ...this.listQuery }
      params.start_at = this.dateRange && this.dateRange[0] ? this.dateRange[0] : ''
      params.end_at = this.dateRange && this.dateRange[1] ? this.dateRange[1] : ''
      return request({ url: '/api/backend/dida/order-monitor', method: 'get', params })
        .then((response) => {
          this.list = response.data.items
          this.total = response.data.total
        })
        .finally(() => { this.listLoading = false })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    resetFilter() {
      this.listQuery = { page: 1, limit: this.listQuery.limit, booking_id: '', system_order_no: '', supplier_order_no: '', trace_id: '', action: '', target_type: '', health_status: '', system_order_status: '' }
      this.dateRange = []
      this.getList()
    },
    handleSizeChange(value) {
      this.listQuery.limit = value
      this.getList()
    },
    handleCurrentChange(value) {
      this.listQuery.page = value
      this.getList()
    },
    openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      request({ url: '/api/backend/dida/order-monitor/detail', method: 'get', params: { booking_id: row.order_id } })
        .then((response) => { this.detail = response.data })
        .finally(() => { this.detailLoading = false })
    },
    supplierName(value) {
      return { jin_jiang: '锦江', long_teng: '龙腾', sui_chuan: '隧穿', li_cheng: '丽呈' }[value] || value || '-'
    },
    supplierTag(value) {
      const tags = { jin_jiang: 'danger', long_teng: 'warning', sui_chuan: 'success', li_cheng: '' }
      return tags[value] !== undefined ? tags[value] : 'info'
    },
    actionName(value) {
      return { New: '新订', Cancel: '取消', Modify: '修改' }[value] || value || '-'
    },
    actionTag(value) {
      return { New: 'success', Cancel: 'danger', Modify: 'warning' }[value] || 'info'
    },
    statusName(value) {
      return { 100: '下单处理中', 101: '下单成功', 102: '下单失败', 201: '取消成功', 202: '取消失败', 203: '取消处理中' }[Number(value)] || String(value || '-')
    },
    statusTag(value) {
      return { 100: 'warning', 101: 'success', 102: 'danger', 201: 'success', 202: 'danger', 203: 'warning' }[Number(value)] || 'info'
    },
    orderStateName(value) {
      return { 1: '预订成功', 2: '已取消', 3: '预订未到', 4: '已入住', 5: '已完成' }[Number(value)] || String(value === null || value === undefined ? '-' : value)
    },
    orderStateTag(value) {
      const tags = { 1: 'success', 2: 'info', 3: 'danger', 4: '', 5: 'success' }
      return tags[Number(value)] !== undefined ? tags[Number(value)] : 'info'
    },
    payStateName(value) {
      return { 0: '未付款', 1: '已付款', 2: '退款处理中', 3: '已退款' }[Number(value)] || String(value === null || value === undefined ? '-' : value)
    },
    payStateTag(value) {
      return { 0: 'warning', 1: 'success', 2: 'warning', 3: 'info' }[Number(value)] || 'info'
    },
    quotaStatusName(value) {
      return { '-1': '已退额度', 0: '未付额度', 1: '已付额度', 2: '部分退额度' }[String(value)] || String(value === null || value === undefined ? '-' : value)
    },
    quotaTag(value) {
      return { '-1': 'danger', 0: 'warning', 1: 'success', 2: 'warning' }[String(value)] || 'info'
    },
    cancelStatusName(value) {
      return { 0: '未取消', 1: '已取消', 2: '取消失败', 3: '取消中' }[String(value)] || String(value === null || value === undefined ? '-' : value)
    },
    cancelTag(value) {
      return { 0: 'info', 1: 'success', 2: 'danger', 3: 'warning' }[String(value)] || 'info'
    },
    accountLogStatusName(value) {
      return { pending: '结算中', available: '已入账', completed: '已完成', failed: '失败', revoked: '已撤回' }[value] || value || '-'
    },
    accountLogTag(value) {
      return { completed: 'success', available: 'success', pending: 'warning', failed: 'danger', revoked: 'info' }[value] || 'info'
    },
    accountDirectionName(value) {
      return Number(value) === 1 ? '支出' : Number(value) === 0 ? '收入' : String(value === null || value === undefined ? '-' : value)
    },
    accountDirectionTag(value) {
      return Number(value) === 1 ? 'danger' : Number(value) === 0 ? 'success' : 'info'
    },
    accountLogTypeName(value) {
      return {
        backend_recharge: '后台充值',
        backend_temporary_recharge: '临时额度充值',
        backend_revoke: '充值撤回',
        backend_temporary_revoke: '临时额度撤回',
        backend_ledger: '后台对账',
        post_order: '下单扣款',
        post_order_revoke: '下单撤回',
        make_up_diff_revoke: '补差撤回',
        backend_order_revoke: '后台订单回退',
        backend_order_deduction: '后台订单扣除',
        ledger_make_up_diff: '账单补差调整',
      }[value] || value || '-'
    },
    quotaDirectionName(row) {
      const before = Number(row.before_quota_value)
      const after = Number(row.after_quota_value)
      if (after > before) return '扣减'
      if (after < before) return '退回'
      return '无变化'
    },
    quotaDirectionTag(row) {
      const before = Number(row.before_quota_value)
      const after = Number(row.after_quota_value)
      return after > before ? 'danger' : after < before ? 'success' : 'info'
    },
    fieldSourceName(value) {
      return { backend: '管理后台', api: 'API接口', timer: '定时任务', system: '系统', reconcile: '对账', reconciliation: '对账' }[value] || value || '-'
    },
    changeCategoryName(value) {
      return { 1: '人工操作', 2: '自动同步', 3: 'API下单', 4: '对账更新', 5: '系统修正' }[Number(value)] || String(value === null || value === undefined ? '-' : value)
    },
    changeCategoryTag(value) {
      const tags = { 1: 'warning', 2: '', 3: 'success', 4: 'danger', 5: 'info' }
      return tags[Number(value)] !== undefined ? tags[Number(value)] : 'info'
    },
    operatorTypeName(value) {
      return { 1: '管理员', 2: 'API', 3: '系统', 4: '员工' }[Number(value)] || String(value === null || value === undefined ? '-' : value)
    },
    changeTypeName(value) {
      return {
        modify_last_arr_time: '修改到店时间',
        change_order_state: '修改订单状态',
        change_system_order_status: '修改系统订单状态',
        change_actual_end_travel_date: '修改实际离店时间',
        change_actual_total_rate: '修改实际账期金额',
        change_manual_override_status: '修改人工覆盖状态',
        store_remark: '修改备注',
        store_quota: '修改额度',
        handle_complete: '处理完成',
        sync_order: '同步订单',
        confirm_cancel: '确认取消',
        order_created: '订单创建',
        post_order_success: '下单成功',
        post_order_fail: '下单失败',
        post_order_revoke: '下单撤回',
        re_order: '重新下单',
        cancel_order: '取消订单',
        cancel_order_callback: '取消订单回调',
        account_log_no_update: '账户流水号更新',
        third_party_order_created: '供应商订单创建',
        cancel_start: '取消开始',
        order_canceled: '订单已取消',
        auto_sync_state: '自动同步状态（定时器）',
        auto_sync_via_api: '自动同步状态（接口）',
        auto_sync_revoke: '自动同步撤回（定时器）',
        auto_sync_revoke_via_api: '自动同步撤回（接口）',
        query_order_update: '查询订单更新',
        third_party_state_sync: '供应商状态同步',
        cancel_revert: '取消回滚',
        confirm_cancel_sync: '确认取消同步',
        sync_status: '同步状态',
        reconcile_update: '对账更新',
        reconcile_handle: '对账处理',
        reconcile_revoke: '对账撤回',
        reconcile_re_handle: '对账重新处理',
        system_correction: '系统修正',
        status_change: '状态变更',
      }[value] || value || '-'
    },
    healthName(value) {
      return { NORMAL: '正常', PROCESSING: '处理中', ERROR: '异常' }[value] || value || '-'
    },
    healthTag(value) {
      return { NORMAL: 'success', PROCESSING: 'warning', ERROR: 'danger' }[value] || 'info'
    },
    money(value) {
      if (value === null || value === undefined || value === '') return '-'
      return Number(value).toFixed(2)
    },
  },
}
</script>

<style scoped>
.scope-alert,
.detail-alert { margin-bottom: 14px; }
.order-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.order-filters .filter-item { width: 180px; margin: 0; }
.order-filters .date-filter { width: 340px; }
.operation-help p { margin: 6px 0; line-height: 1.5; }
.section-title .el-tag { margin-left: 8px; vertical-align: middle; }
.el-timeline code,
.el-timeline .el-tag,
.el-timeline span { margin-right: 8px; }
.metric-cards { margin: 12px 0 18px; text-align: center; }
.metric-cards b { display: block; color: #409eff; font-size: 18px; }
.metric-cards span { display: block; margin-top: 6px; color: #606266; font-size: 12px; }
code { color: #409eff; }
.error-text { color: #f56c6c; }
</style>
